import { useCallback, useRef } from 'react';

export function useUploadWorkflow({
  config,
  caseFilesRef,
  mediaItems,
  mediaItemsRef,
  pendingUploadItems,
  interactionLocked,
  inlineError,
  caseUploadBlockedMessage,
  isUploadBlockedByCaseStatus,
  loadCaseFiles,
  queueCaseFilePolling,
  shouldCountLocalUploadItem,
  getUploadLimitValidationMessage,
  hasPendingAnyScanWork,
  buildProcessingDeadlineBatchItem,
  collectDeadlineMatchedRemoteScanCandidates,
  processTimedOutRemoteScanCandidates,
  isTerminalScanStatus,
  uploadMultipartFile,
  persistMediaRecord,
  persistTranscriptRecord,
  updateItemStatus,
  updateMediaItem,
  setMediaItems,
  setInlineError,
  setCompletionDialog,
  setIsUiLocked,
  debugLog,
  uiMessages,
  processingMaxTimeSeconds,
  withReason,
  showUploadBlockedDialog
}) {
  const uploadRunRef = useRef(0);

  const createProcessingDeadlineError = useCallback(() => {
    const error = new Error(uiMessages.scanTimeout);
    error.code = 'PROCESSING_DEADLINE_EXCEEDED';
    return error;
  }, [uiMessages.scanTimeout]);

  const isProcessingDeadlineError = useCallback((error) => error?.code === 'PROCESSING_DEADLINE_EXCEEDED', []);

  const runPromiseWithDeadline = useCallback(async (promiseFactory, deadlineAtMs) => {
    if (!Number.isFinite(deadlineAtMs)) {
      return promiseFactory();
    }

    const remainingMs = deadlineAtMs - Date.now();
    if (remainingMs <= 0) {
      throw createProcessingDeadlineError();
    }

    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = globalThis.setTimeout(() => {
        reject(createProcessingDeadlineError());
      }, remainingMs);
    });

    try {
      return await Promise.race([promiseFactory(), timeoutPromise]);
    } finally {
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
    }
  }, [createProcessingDeadlineError]);

  const markLocalUploadItemTimedOut = useCallback((itemId) => {
    if (!itemId) {
      return false;
    }

    let hasChanges = false;
    setMediaItems((currentItems) => {
      const nextItems = currentItems.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        const mediaUploadPending = item.mediaUploadStatus !== 'completed' && item.mediaUploadStatus !== 'error';
        const transcriptUploadPending = item.transcriptEnabled
          && item.transcriptUploadStatus !== 'completed'
          && item.transcriptUploadStatus !== 'error'
          && item.transcriptUploadStatus !== 'not-applicable';
        const mediaScanPending = item.mediaUploadStatus === 'completed' && !isTerminalScanStatus(item.scanStatus);
        const transcriptScanPending = item.transcriptEnabled
          && item.transcriptUploadStatus === 'completed'
          && !isTerminalScanStatus(item.transcriptScanStatus);

        if (!mediaUploadPending && !transcriptUploadPending && !mediaScanPending && !transcriptScanPending) {
          return item;
        }

        hasChanges = true;
        return {
          ...item,
          mediaUploadStatus: mediaUploadPending ? 'error' : item.mediaUploadStatus,
          scanStatus: (mediaUploadPending || mediaScanPending) ? 'failed' : item.scanStatus,
          transcriptUploadStatus: transcriptUploadPending ? 'error' : item.transcriptUploadStatus,
          transcriptScanStatus: (transcriptUploadPending || transcriptScanPending) ? 'failed' : item.transcriptScanStatus,
          uploadComplete: false,
          progress: 100,
          latestError: uiMessages.scanTimeout
        };
      });

      return nextItems;
    });

    return hasChanges;
  }, [isTerminalScanStatus, setMediaItems, uiMessages.scanTimeout]);

  const failUploadItemAtProcessingDeadline = useCallback(async (itemId, deadlineItem) => {
    const localTimedOut = markLocalUploadItemTimedOut(itemId);
    let remoteTimedOut = false;

    if (config.uploadS3ApiBaseUrl && config.caseId && deadlineItem) {
      const remoteFiles = await loadCaseFiles(false);
      const timedOutCandidates = Array.isArray(remoteFiles)
        ? collectDeadlineMatchedRemoteScanCandidates(remoteFiles, [deadlineItem])
        : [];

      if (timedOutCandidates.length > 0) {
        remoteTimedOut = await processTimedOutRemoteScanCandidates(timedOutCandidates);
        if (remoteTimedOut) {
          await loadCaseFiles(false);
        }
      }
    }

    if (localTimedOut || remoteTimedOut) {
      setInlineError(uiMessages.scanTimeout);
    }

    return localTimedOut || remoteTimedOut;
  }, [collectDeadlineMatchedRemoteScanCandidates, config.caseId, config.uploadS3ApiBaseUrl, loadCaseFiles, markLocalUploadItemTimedOut, processTimedOutRemoteScanCandidates, setInlineError, uiMessages.scanTimeout]);

  const validateUploadReady = useCallback(() => {
    if (!config.uploadS3ApiBaseUrl) {
      return 'uploadS3ApiBaseUrl is not configured';
    }

    if (config.authError) {
      return config.authError;
    }

    const callbackToken = config.callbackToken || config.xCallbackToken || '';
    if (!config.clientId || !callbackToken) {
      return uiMessages.authenticatedUploadRequired;
    }

    if (!config.applicationNumber) {
      return uiMessages.applicationNumberRequired;
    }

    if (!config.caseId) {
      return uiMessages.caseIdRequired;
    }

    if (isUploadBlockedByCaseStatus) {
      return caseUploadBlockedMessage;
    }

    const limitMessage = getUploadLimitValidationMessage(mediaItemsRef.current, caseFilesRef.current);
    if (limitMessage) {
      return limitMessage;
    }

    if (inlineError) {
      return inlineError;
    }

    if (pendingUploadItems.length === 0) {
      return mediaItems.length > 0 ? uiMessages.allFilesUploaded : uiMessages.selectMediaFirst;
    }

    if (mediaItemsRef.current.some((item) => item.transcriptEnabled && !item.transcriptFile)) {
      return uiMessages.transcriptRequired;
    }

    return '';
  }, [caseFilesRef, caseUploadBlockedMessage, config.applicationNumber, config.authError, config.callbackToken, config.caseId, config.clientId, config.uploadS3ApiBaseUrl, config.xCallbackToken, getUploadLimitValidationMessage, inlineError, isUploadBlockedByCaseStatus, mediaItems.length, mediaItemsRef, pendingUploadItems.length, uiMessages.allFilesUploaded, uiMessages.applicationNumberRequired, uiMessages.authenticatedUploadRequired, uiMessages.caseIdRequired, uiMessages.selectMediaFirst, uiMessages.transcriptRequired]);

  const buildUploadQueue = useCallback(() => mediaItemsRef.current
    .filter((item) => shouldCountLocalUploadItem(item, caseFilesRef.current))
    .map((item) => ({ ...item })), [caseFilesRef, mediaItemsRef, shouldCountLocalUploadItem]);

  const resetUploadQueueState = useCallback((uploadQueueIds) => {
    setMediaItems((current) => current.map((item) => {
      if (!uploadQueueIds.has(item.id)) {
        return item;
      }

      return {
        ...item,
        mediaUploadStatus: 'idle',
        scanStatus: 'pending',
        transcriptUploadStatus: item.transcriptEnabled ? 'idle' : 'not-applicable',
        transcriptScanStatus: item.transcriptEnabled ? 'idle' : 'not-applicable',
        mediaId: '',
        transcriptFileId: '',
        uploadComplete: false,
        progress: 0,
        latestError: ''
      };
    }));
  }, [setMediaItems]);

  const uploadMediaForItem = useCallback(async ({ item, currentRunId, deadlineAtMs, deadlineItem }) => {
    const storedMediaFileName = item.mediaFile.name;
    updateItemStatus(item.id, {
      mediaUploadStatus: 'uploading',
      scanStatus: 'pending',
      progress: 5,
      latestError: ''
    });

    let mediaUploadTimedOut = false;
    let mediaUploadResult;
    try {
      mediaUploadResult = await runPromiseWithDeadline(() => uploadMultipartFile(item.mediaFile, {
        applicationNumber: config.applicationNumber,
        uploadedByUserId: config.userId || 'web-control',
        originalFileName: item.mediaFile.name,
        storedFileName: storedMediaFileName,
        fileType: 'Media',
        mimeType: item.mediaFile.type || 'application/octet-stream',
        recordStatus: 'Draft',
        description: item.description || ''
      }, (progress) => {
        if (mediaUploadTimedOut || currentRunId !== uploadRunRef.current) {
          return;
        }

        updateItemStatus(item.id, { progress });
      }), deadlineAtMs);
    } catch (error) {
      if (!isProcessingDeadlineError(error)) {
        throw error;
      }

      mediaUploadTimedOut = true;
      await failUploadItemAtProcessingDeadline(item.id, deadlineItem);
      return { timedOut: true };
    }

    const persistedMediaFileName = mediaUploadResult?.completeData?.storedFileName
      || mediaUploadResult?.completeData?.fileName
      || storedMediaFileName;
    debugLog('upload-media-persist-name-resolution', {
      localItemId: item.id,
      originalFileName: item?.mediaFile?.name,
      startStoredFileName: storedMediaFileName,
      completeResponseStoredFileName: mediaUploadResult?.completeData?.storedFileName,
      completeResponseFileName: mediaUploadResult?.completeData?.fileName,
      chosenPersistedMediaFileName: persistedMediaFileName
    });

    const predictedMediaId = mediaUploadResult?.predictedMediaId || '';
    if (predictedMediaId) {
      updateItemStatus(item.id, { mediaId: predictedMediaId });
    }

    const mediaRecord = await runPromiseWithDeadline(() => persistMediaRecord({
      ...item,
      mediaId: predictedMediaId || item.mediaId || ''
    }, persistedMediaFileName, mediaUploadResult?.completeData), deadlineAtMs);
    const mediaId = mediaRecord?.mediaId || mediaRecord?.MediaId || '';

    updateItemStatus(item.id, {
      mediaUploadStatus: 'completed',
      mediaId,
      mediaStoredFileName: persistedMediaFileName,
      uploadComplete: !item.transcriptEnabled,
      progress: 100
    });

    deadlineItem.mediaId = mediaId;
    deadlineItem.mediaStoredFileName = persistedMediaFileName;
    return { timedOut: false, mediaId };
  }, [config.applicationNumber, config.userId, debugLog, failUploadItemAtProcessingDeadline, isProcessingDeadlineError, persistMediaRecord, runPromiseWithDeadline, updateItemStatus, uploadMultipartFile]);

  const uploadTranscriptForItem = useCallback(async ({ item, currentRunId, deadlineAtMs, deadlineItem, mediaId }) => {
    if (!item.transcriptEnabled || !item.transcriptFile) {
      return { timedOut: false };
    }

    const storedTranscriptFileName = item.transcriptFile.name;
    updateItemStatus(item.id, {
      transcriptUploadStatus: 'uploading',
      transcriptScanStatus: 'pending',
      progress: Math.max(88, mediaItemsRef.current.find((currentItem) => currentItem.id === item.id)?.progress ?? 0)
    });

    if (!mediaId) {
      throw new Error('Transcript upload requires an existing mediaId.');
    }

    let transcriptUploadTimedOut = false;
    let transcriptUploadResult;
    try {
      transcriptUploadResult = await runPromiseWithDeadline(() => uploadMultipartFile(item.transcriptFile, {
        applicationNumber: config.applicationNumber,
        uploadedByUserId: config.userId || 'web-control',
        originalFileName: item.transcriptFile.name,
        storedFileName: storedTranscriptFileName,
        fileType: 'Transcript',
        mimeType: item.transcriptFile.type || 'application/octet-stream',
        recordStatus: 'Draft',
        mediaId,
        description: item.transcriptDescription || ''
      }, (progress) => {
        if (transcriptUploadTimedOut || currentRunId !== uploadRunRef.current) {
          return;
        }

        updateItemStatus(item.id, { progress: Math.max(88, progress) });
      }), deadlineAtMs);
    } catch (error) {
      if (!isProcessingDeadlineError(error)) {
        throw error;
      }

      transcriptUploadTimedOut = true;
      await failUploadItemAtProcessingDeadline(item.id, deadlineItem);
      return { timedOut: true };
    }

    const persistedTranscriptFileName = transcriptUploadResult?.completeData?.storedFileName
      || transcriptUploadResult?.completeData?.fileName
      || storedTranscriptFileName;
    debugLog('upload-transcript-persist-name-resolution', {
      localItemId: item.id,
      originalFileName: item?.transcriptFile?.name,
      startStoredFileName: storedTranscriptFileName,
      completeResponseStoredFileName: transcriptUploadResult?.completeData?.storedFileName,
      completeResponseFileName: transcriptUploadResult?.completeData?.fileName,
      chosenPersistedTranscriptFileName: persistedTranscriptFileName,
      linkedMediaId: mediaId
    });

    const transcriptRecord = await runPromiseWithDeadline(() => persistTranscriptRecord(item, mediaId, persistedTranscriptFileName, transcriptUploadResult?.completeData), deadlineAtMs);
    const transcriptFileId = transcriptRecord?.transcriptFileId || transcriptRecord?.TranscriptFileId || '';
    updateItemStatus(item.id, {
      transcriptUploadStatus: 'completed',
      transcriptFileId,
      transcriptStoredFileName: persistedTranscriptFileName,
      uploadComplete: true,
      progress: 100
    });

    deadlineItem.transcriptFileId = transcriptFileId;
    deadlineItem.transcriptStoredFileName = persistedTranscriptFileName;
    return { timedOut: false };
  }, [config.applicationNumber, config.userId, debugLog, failUploadItemAtProcessingDeadline, isProcessingDeadlineError, mediaItemsRef, persistTranscriptRecord, runPromiseWithDeadline, updateItemStatus, uploadMultipartFile]);

  const uploadSingleQueueItem = useCallback(async (item, currentRunId) => {
    if (currentRunId !== uploadRunRef.current) {
      throw new Error(uiMessages.uploadCancelled);
    }

    const deadlineAtMs = processingMaxTimeSeconds > 0
      ? Date.now() + (processingMaxTimeSeconds * 1000)
      : Number.POSITIVE_INFINITY;
    const deadlineItem = buildProcessingDeadlineBatchItem({
      ...item,
      caseId: config.caseId
    });

    try {
      const mediaResult = await uploadMediaForItem({ item, currentRunId, deadlineAtMs, deadlineItem });
      if (mediaResult?.timedOut) {
        return;
      }

      const transcriptResult = await uploadTranscriptForItem({
        item,
        currentRunId,
        deadlineAtMs,
        deadlineItem,
        mediaId: mediaResult?.mediaId || ''
      });
      if (transcriptResult?.timedOut) {
        return;
      }
    } catch (error) {
      if (!isProcessingDeadlineError(error)) {
        throw error;
      }

      await failUploadItemAtProcessingDeadline(item.id, deadlineItem);
    }
  }, [buildProcessingDeadlineBatchItem, config.caseId, failUploadItemAtProcessingDeadline, isProcessingDeadlineError, processingMaxTimeSeconds, uiMessages.uploadCancelled, uploadMediaForItem, uploadTranscriptForItem]);

  const handleUploadClick = useCallback(async () => {
    if (interactionLocked) {
      return;
    }

    if (config.uploadS3ApiBaseUrl && config.caseId) {
      await loadCaseFiles(false);
    }

    const configError = validateUploadReady();
    if (configError) {
      showUploadBlockedDialog(configError);
      return;
    }

    const limitMessage = getUploadLimitValidationMessage(mediaItemsRef.current, caseFilesRef.current);
    if (limitMessage) {
      showUploadBlockedDialog(limitMessage);
      return;
    }

    try {
      setInlineError('');
      setCompletionDialog(null);
      setIsUiLocked(true);
      uploadRunRef.current += 1;
      const currentRunId = uploadRunRef.current;
      const uploadQueue = buildUploadQueue();
      const uploadQueueIds = new Set(uploadQueue.map((item) => item.id));
      resetUploadQueueState(uploadQueueIds);

      for (const item of uploadQueue) {
        await uploadSingleQueueItem(item, currentRunId);
      }

      setCompletionDialog(null);
    } catch (error) {
      setInlineError("We're having trouble uploading your files right now. Please wait a moment and try again");
      setCompletionDialog(null);
      if (mediaItemsRef.current[0]?.id) {
        updateMediaItem(mediaItemsRef.current[0].id, (item) => ({
          ...item,
          latestError: error?.message || 'Unknown error',
          mediaUploadStatus: item.mediaUploadStatus === 'uploading' ? 'error' : item.mediaUploadStatus
        }));
      }
    } finally {
      await loadCaseFiles(false);
      if (hasPendingAnyScanWork(mediaItemsRef.current, caseFilesRef.current)) {
        queueCaseFilePolling();
      }
      setIsUiLocked(false);
    }
  }, [buildUploadQueue, caseFilesRef, config.caseId, config.uploadS3ApiBaseUrl, getUploadLimitValidationMessage, hasPendingAnyScanWork, interactionLocked, loadCaseFiles, mediaItemsRef, queueCaseFilePolling, resetUploadQueueState, setCompletionDialog, setInlineError, setIsUiLocked, showUploadBlockedDialog, uploadSingleQueueItem, updateMediaItem, validateUploadReady]);

  return {
    handleUploadClick
  };
}
